/**
 * A minimal, dependency-free `.xlsx` writer.
 *
 * Why not a library: the cashier is an offline-first PWA whose every built
 * asset is precached by the service worker, and the smallest real spreadsheet
 * library is about a megabyte of that budget for one button. The subset of
 * SpreadsheetML a report needs — inline strings, a fixed style catalogue,
 * column widths, merges, frozen panes — is a few hundred lines, so it lives
 * here instead.
 *
 * Output is a STORED (uncompressed) zip. Excel, LibreOffice, Numbers and
 * Google Sheets all accept method-0 entries; skipping deflate is what keeps
 * this file free of a compression dependency. A report of a few thousand rows
 * is well under a megabyte either way.
 *
 * Text is written as INLINE strings rather than a shared-string table: reports
 * repeat very little text, and inline strings keep the writer a single pass
 * with no global index to keep in sync.
 */

// ─────────────────────────────────────────────────────────────────────────
// Public model
// ─────────────────────────────────────────────────────────────────────────

/**
 * The style catalogue. Every cell picks one of these rather than carrying its
 * own font/fill/border, which is what keeps one workbook looking like one
 * document.
 */
export type XlsxStyle =
  | 'title' | 'subtitle' | 'section' | 'header' | 'note'
  | 'text' | 'textCenter' | 'int' | 'money' | 'qty' | 'pct'
  | 'totalText' | 'totalInt' | 'totalMoney' | 'totalQty' | 'totalPct';

export interface XlsxCell {
  value?: string | number | null;
  style?: XlsxStyle;
  /** Merge with the `span - 1` cells to the right (1 = no merge). */
  span?: number;
}

/** A cell, or the bare value when the row's default style is good enough. */
export type XlsxCellInput = XlsxCell | string | number | null | undefined;

export interface XlsxRow {
  cells: XlsxCellInput[];
  /** Row height in points; omitted rows use the sheet default. */
  height?: number;
}

export interface XlsxSheet {
  name: string;
  rows: XlsxRow[];
  /**
   * Column widths in characters. `null`/missing = auto-size from the content
   * (which is what every report uses).
   */
  widths?: (number | null | undefined)[];
  /** Arabic session: the sheet itself reads right-to-left. */
  rightToLeft?: boolean;
  /** Rows kept on screen while the rest scrolls (the title + header block). */
  freezeRows?: number;
  /** Landscape + fit-to-width; wide reports set this. */
  landscape?: boolean;
}

export interface XlsxWorkbook {
  sheets: XlsxSheet[];
  /**
   * Decimals on the `money` / `totalMoney` styles — the tenant currency's
   * precision (3 on KWD, 2 elsewhere), so a spreadsheet total agrees with the
   * receipt it summarises.
   */
  moneyDecimals?: number;
}

/** Widths are clamped into this band so one long name cannot swallow a sheet. */
const MIN_WIDTH = 7;
const MAX_WIDTH = 52;

// ─────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────

/**
 * `cellXfs` index per style name. The order here IS the order written into
 * styles.xml below — change one and you must change the other.
 */
const STYLE_INDEX: Record<XlsxStyle, number> = {
  title: 1,
  subtitle: 2,
  section: 3,
  header: 4,
  text: 5,
  textCenter: 6,
  int: 7,
  money: 8,
  qty: 9,
  pct: 10,
  totalText: 11,
  totalInt: 12,
  totalMoney: 13,
  totalQty: 14,
  totalPct: 15,
  note: 16,
};

/** Styles whose cells hold a number, so a numeric value is written as one. */
const NUMERIC_STYLES = new Set<XlsxStyle>([
  'int', 'money', 'qty', 'pct', 'totalInt', 'totalMoney', 'totalQty', 'totalPct',
]);

function stylesXml(moneyDecimals: number): string {
  const moneyFmt = moneyDecimals > 0 ? `#,##0.${'0'.repeat(moneyDecimals)}` : '#,##0';
  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<numFmts count="4">
<numFmt numFmtId="164" formatCode="${esc(moneyFmt)}"/>
<numFmt numFmtId="165" formatCode="#,##0.000"/>
<numFmt numFmtId="166" formatCode="0.00&quot;%&quot;"/>
<numFmt numFmtId="167" formatCode="#,##0"/>
</numFmts>
<fonts count="6">
<font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="16"/><color rgb="FF1F3864"/><name val="Calibri"/><family val="2"/></font>
<font><sz val="10"/><color rgb="FF595959"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="11"/><color rgb="FF1F3864"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="12"/><color rgb="FF1F3864"/><name val="Calibri"/><family val="2"/></font>
<font><b/><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>
</fonts>
<fills count="5">
<fill><patternFill patternType="none"/></fill>
<fill><patternFill patternType="gray125"/></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFD9E2F3"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFEDEDED"/><bgColor indexed="64"/></patternFill></fill>
<fill><patternFill patternType="solid"><fgColor rgb="FFF2F2F2"/><bgColor indexed="64"/></patternFill></fill>
</fills>
<borders count="3">
<border><left/><right/><top/><bottom/><diagonal/></border>
<border><left style="thin"><color rgb="FFBFBFBF"/></left><right style="thin"><color rgb="FFBFBFBF"/></right><top style="thin"><color rgb="FFBFBFBF"/></top><bottom style="thin"><color rgb="FFBFBFBF"/></bottom><diagonal/></border>
<border><left style="thin"><color rgb="FFBFBFBF"/></left><right style="thin"><color rgb="FFBFBFBF"/></right><top style="medium"><color rgb="FF8EA9DB"/></top><bottom style="thin"><color rgb="FFBFBFBF"/></bottom><diagonal/></border>
</borders>
<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>
<cellXfs count="17">
<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>
<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="center" vertical="center"/></xf>
<xf numFmtId="0" fontId="4" fillId="3" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="center"/></xf>
<xf numFmtId="0" fontId="3" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="center" wrapText="1"/></xf>
<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1" applyAlignment="1"><alignment horizontal="center" vertical="center" wrapText="1"/></xf>
<xf numFmtId="167" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
<xf numFmtId="164" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
<xf numFmtId="165" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
<xf numFmtId="166" fontId="0" fillId="0" borderId="1" xfId="0" applyNumberFormat="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
<xf numFmtId="0" fontId="5" fillId="4" borderId="2" xfId="0" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="left" vertical="center" wrapText="1"/></xf>
<xf numFmtId="167" fontId="5" fillId="4" borderId="2" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
<xf numFmtId="164" fontId="5" fillId="4" borderId="2" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
<xf numFmtId="165" fontId="5" fillId="4" borderId="2" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
<xf numFmtId="166" fontId="5" fillId="4" borderId="2" xfId="0" applyNumberFormat="1" applyFont="1" applyFill="1" applyBorder="1" applyAlignment="1"><alignment horizontal="right" vertical="center"/></xf>
<xf numFmtId="0" fontId="2" fillId="0" borderId="0" xfId="0" applyFont="1" applyAlignment="1"><alignment horizontal="left" vertical="center" wrapText="1" indent="1"/></xf>
</cellXfs>
<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>
</styleSheet>`;
}

// ─────────────────────────────────────────────────────────────────────────
// Sheet rendering
// ─────────────────────────────────────────────────────────────────────────

/** 0-based column index → `A`, `B`, … `AA`. */
export function columnName(index: number): string {
  let n = index;
  let out = '';
  do {
    out = String.fromCharCode(65 + (n % 26)) + out;
    n = Math.floor(n / 26) - 1;
  } while (n >= 0);
  return out;
}

function normalize(cell: XlsxCellInput): XlsxCell {
  if (cell == null) return {};
  if (typeof cell === 'string' || typeof cell === 'number') return { value: cell };
  return cell;
}

/** XML 1.0 will not carry the C0 controls, and a stray one corrupts the file. */
function esc(s: string): string {
  return s
    // eslint-disable-next-line no-control-regex
    .replace(/[ --]/g, '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * How wide a cell's text renders, in Excel's character units. Numbers are
 * measured at their formatted length (thousands separators included) rather
 * than `String(n)`, which is what stops a money column coming out too narrow.
 */
function cellWidth(cell: XlsxCell, moneyDecimals: number): number {
  const v = cell.value;
  if (v == null || v === '') return 0;
  if (typeof v === 'number') {
    const style = (cell.style ?? 'text').toLowerCase();
    const decimals = style.includes('money') ? moneyDecimals
      : style.includes('qty') ? 3
        : style.includes('pct') ? 2 : 0;
    const whole = Math.floor(Math.abs(v)).toLocaleString('en-US').length;
    return whole + (decimals > 0 ? decimals + 1 : 0) + (v < 0 ? 1 : 0) + (style.includes('pct') ? 1 : 0);
  }
  // Longest line — a wrapped cell is sized by its widest word-run, not its total.
  return Math.max(...String(v).split('\n').map((line) => line.length));
}

function autoWidths(sheet: XlsxSheet, moneyDecimals: number): number[] {
  const measured: number[] = [];
  let columns = sheet.widths?.length ?? 0;
  for (const row of sheet.rows) {
    let col = 0;
    for (const raw of row.cells) {
      const cell = normalize(raw);
      const span = Math.max(1, cell.span ?? 1);
      // A merged cell (a title, a note) spans several columns and must not
      // drag any single one of them wide.
      if (span === 1) {
        measured[col] = Math.max(measured[col] ?? 0, cellWidth(cell, moneyDecimals));
      }
      col += span;
      columns = Math.max(columns, col);
    }
  }
  const out: number[] = [];
  for (let i = 0; i < columns; i += 1) {
    const explicit = sheet.widths?.[i];
    out.push(explicit != null
      ? explicit
      : Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, Math.round((measured[i] ?? 0) + 3))));
  }
  return out;
}

function sheetXml(sheet: XlsxSheet, moneyDecimals: number): string {
  const widths = autoWidths(sheet, moneyDecimals);
  const merges: string[] = [];
  const rowsXml: string[] = [];
  let maxCol = 1;

  sheet.rows.forEach((row, r) => {
    const rowNum = r + 1;
    const cells: string[] = [];
    let col = 0;
    for (const raw of row.cells) {
      const cell = normalize(raw);
      const span = Math.max(1, cell.span ?? 1);
      const ref = `${columnName(col)}${rowNum}`;
      const s = cell.style ? STYLE_INDEX[cell.style] : 0;
      const sAttr = s ? ` s="${s}"` : '';
      const v = cell.value;
      const numeric = typeof v === 'number' && Number.isFinite(v)
        && (cell.style == null || NUMERIC_STYLES.has(cell.style));
      if (numeric) {
        cells.push(`<c r="${ref}"${sAttr}><v>${v}</v></c>`);
      } else if (v != null && v !== '') {
        cells.push(`<c r="${ref}"${sAttr} t="inlineStr"><is><t xml:space="preserve">${esc(String(v))}</t></is></c>`);
      } else if (s) {
        cells.push(`<c r="${ref}"${sAttr}/>`);
      }
      if (span > 1) {
        // The covered cells must exist and carry the same style, or the merge
        // draws its fill and borders over only its first column.
        for (let k = 1; k < span; k += 1) {
          if (s) cells.push(`<c r="${columnName(col + k)}${rowNum}"${sAttr}/>`);
        }
        merges.push(`<mergeCell ref="${ref}:${columnName(col + span - 1)}${rowNum}"/>`);
      }
      col += span;
    }
    maxCol = Math.max(maxCol, col);
    const ht = row.height ? ` ht="${row.height}" customHeight="1"` : '';
    rowsXml.push(`<row r="${rowNum}"${ht}>${cells.join('')}</row>`);
  });

  const colsXml = widths.length === 0 ? '' : `<cols>${widths
    .map((w, i) => `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>`)
    .join('')}</cols>`;

  const freeze = sheet.freezeRows
    ? `<pane ySplit="${sheet.freezeRows}" topLeftCell="A${sheet.freezeRows + 1}" activePane="bottomLeft" state="frozen"/><selection pane="bottomLeft"/>`
    : '';
  const rtl = sheet.rightToLeft ? ' rightToLeft="1"' : '';
  const dimension = `A1:${columnName(Math.max(0, maxCol - 1))}${Math.max(1, sheet.rows.length)}`;

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">
<sheetPr><pageSetUpPr fitToPage="1"/></sheetPr>
<dimension ref="${dimension}"/>
<sheetViews><sheetView showGridLines="0"${rtl} workbookViewId="0">${freeze}</sheetView></sheetViews>
<sheetFormatPr defaultRowHeight="15"/>
${colsXml}
<sheetData>${rowsXml.join('')}</sheetData>
${merges.length > 0 ? `<mergeCells count="${merges.length}">${merges.join('')}</mergeCells>` : ''}
<pageMargins left="0.4" right="0.4" top="0.5" bottom="0.5" header="0.3" footer="0.3"/>
<pageSetup paperSize="9" orientation="${sheet.landscape ? 'landscape' : 'portrait'}" fitToWidth="1" fitToHeight="0"/>
</worksheet>`;
}

/** Excel forbids these in a sheet name, and caps it at 31 characters. */
function safeSheetName(name: string, index: number): string {
  const cleaned = name.replace(/[\\/?*[\]:]/g, ' ').trim().slice(0, 31);
  return cleaned || `Sheet${index + 1}`;
}

// ─────────────────────────────────────────────────────────────────────────
// Package
// ─────────────────────────────────────────────────────────────────────────

/** Build the workbook. Returns the raw `.xlsx` bytes. */
export function buildXlsx(workbook: XlsxWorkbook): Uint8Array {
  const moneyDecimals = workbook.moneyDecimals ?? 2;
  const sheets = workbook.sheets.length > 0 ? workbook.sheets : [{ name: 'Sheet1', rows: [] }];
  const names = sheets.map((s, i) => safeSheetName(s.name, i));

  const files: { path: string; data: string }[] = [
    {
      path: '[Content_Types].xml',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">
<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>
<Default Extension="xml" ContentType="application/xml"/>
<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>
<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>
${sheets.map((_, i) => `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`).join('\n')}
</Types>`,
    },
    {
      path: '_rels/.rels',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>
</Relationships>`,
    },
    {
      path: 'xl/workbook.xml',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">
<sheets>${names.map((n, i) => `<sheet name="${esc(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`).join('')}</sheets>
</workbook>`,
    },
    {
      path: 'xl/_rels/workbook.xml.rels',
      data: `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">
${sheets.map((_, i) => `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`).join('\n')}
<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>
</Relationships>`,
    },
    { path: 'xl/styles.xml', data: stylesXml(moneyDecimals) },
    ...sheets.map((s, i) => ({ path: `xl/worksheets/sheet${i + 1}.xml`, data: sheetXml(s, moneyDecimals) })),
  ];

  return zip(files.map((f) => ({ path: f.path, bytes: utf8(f.data) })));
}

function utf8(s: string): Uint8Array {
  return new TextEncoder().encode(s);
}

// ─────────────────────────────────────────────────────────────────────────
// ZIP (stored entries only)
// ─────────────────────────────────────────────────────────────────────────

let CRC_TABLE: Uint32Array | null = null;
function crcTable(): Uint32Array {
  if (CRC_TABLE) return CRC_TABLE;
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n += 1) {
    let c = n;
    for (let k = 0; k < 8; k += 1) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  CRC_TABLE = t;
  return t;
}

function crc32(bytes: Uint8Array): number {
  const t = crcTable();
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) c = t[(c ^ bytes[i]!) & 0xff]! ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

/**
 * A fixed timestamp (2020-01-01 00:00) rather than "now": the same report over
 * the same range then produces a byte-identical file, which is what makes the
 * writer's own tests meaningful.
 */
const DOS_TIME = 0;
const DOS_DATE = ((2020 - 1980) << 9) | (1 << 5) | 1;

function zip(entries: { path: string; bytes: Uint8Array }[]): Uint8Array {
  const locals: Uint8Array[] = [];
  const centrals: Uint8Array[] = [];
  let offset = 0;

  for (const entry of entries) {
    const name = utf8(entry.path);
    const crc = crc32(entry.bytes);
    const size = entry.bytes.length;

    const local = new Uint8Array(30 + name.length + size);
    const lv = new DataView(local.buffer);
    lv.setUint32(0, 0x04034b50, true);
    lv.setUint16(4, 20, true);        // version needed
    lv.setUint16(6, 0x0800, true);    // UTF-8 names
    lv.setUint16(8, 0, true);         // method: stored
    lv.setUint16(10, DOS_TIME, true);
    lv.setUint16(12, DOS_DATE, true);
    lv.setUint32(14, crc, true);
    lv.setUint32(18, size, true);
    lv.setUint32(22, size, true);
    lv.setUint16(26, name.length, true);
    lv.setUint16(28, 0, true);
    local.set(name, 30);
    local.set(entry.bytes, 30 + name.length);
    locals.push(local);

    const central = new Uint8Array(46 + name.length);
    const cv = new DataView(central.buffer);
    cv.setUint32(0, 0x02014b50, true);
    cv.setUint16(4, 20, true);        // version made by
    cv.setUint16(6, 20, true);        // version needed
    cv.setUint16(8, 0x0800, true);
    cv.setUint16(10, 0, true);
    cv.setUint16(12, DOS_TIME, true);
    cv.setUint16(14, DOS_DATE, true);
    cv.setUint32(16, crc, true);
    cv.setUint32(20, size, true);
    cv.setUint32(24, size, true);
    cv.setUint16(28, name.length, true);
    cv.setUint32(42, offset, true);
    central.set(name, 46);
    centrals.push(central);

    offset += local.length;
  }

  const centralSize = centrals.reduce((s, c) => s + c.length, 0);
  const end = new Uint8Array(22);
  const ev = new DataView(end.buffer);
  ev.setUint32(0, 0x06054b50, true);
  ev.setUint16(8, entries.length, true);
  ev.setUint16(10, entries.length, true);
  ev.setUint32(12, centralSize, true);
  ev.setUint32(16, offset, true);

  const total = offset + centralSize + end.length;
  const out = new Uint8Array(total);
  let p = 0;
  for (const chunk of [...locals, ...centrals, end]) {
    out.set(chunk, p);
    p += chunk.length;
  }
  return out;
}
